import React, { useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { useModalAction } from "src/components/modal-views/context";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import client from "../../api";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import { newPaginationInput } from "src/api/types";
import { getErrorMessage } from "src/lib/utils";
import { Table } from "antd";
import moment from "moment";

const CardConversionHistory = () => {
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const { openModal, closeModal } = useModalAction();

  const { canView, canList } = usePermissions(
    permissionConstants.CARD_CONVERSION_RATES_HISTORY
  );
  const canViewHistory = canView || canList;

  const [dataSource, setDataSource] = useState<any[]>([]);
  const [searchParams, setSearchParams] = useState<newPaginationInput>({
    limit: LIST_PAGE_SIZE,
    page: 1,
  });
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState<boolean>(false);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 768 ? true : false);
  }
  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);
  const { mutate: getCurrencyConversionRateslistHistory, isLoading } =
    useMutation(
      client.cardConversionRates.getCurrencyConversionRateslistHistory,
      {
        onSuccess: (data: any) => {
          const dataObj = data?.data?.data;
          setTotal(data?.data?.pagination?.total);
          setDataSource(
            dataObj.map((item: any) => ({
              key: item.id,
              id: item.id,
              merchant_id: item.merchant_id,
              src_currency_code: item.src_currency_code,
              dst_currency_code: item.dst_currency_code,
              rate: item.rate,
              margin_percent: item.margin_percent,
              final_rate: item.final_rate,
              source: item.source,
              sync_type: item.sync_type,
              bank_status: item.bank_status,

              synced_at: item.synced_at,
              createdAt: item.createdAt,
            })),
          );
        },
        onError: (error: any) => {
          if (error.code === "ERR_NETWORK") {
            setAlertText(NetworkDisconnectMsg);
          } else {
            if (error.response) {
              setAlertText(getErrorMessage(error.response.data));
            } else {
              setAlertText(APISomethingWrongMsg);
            }
          }
          setIsAlertOpened(true);
        },
      },
    );

  const onPageChange = (page: number, pageSize: number) => {
    setSearchParams({
      ...searchParams,
      page: page,
    });
  };

  useEffect(() => {
    if (!isMounted || !canViewHistory) {
      return;
    }
    getCurrencyConversionRateslistHistory({
      ...searchParams,
      // start_date: startDate
      //   ? moment(startDate).format("yyyy-MM-DD")
      //   : undefined,
      // end_date: endDate ? moment(endDate).format("yyyy-MM-DD") : undefined,
    });
  }, [isMounted, canViewHistory, searchParams.page]);

  const columns = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          ID
        </p>
      ),
      dataIndex: "id",
      key: "id",
      render: (value: any) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Merchant ID
        </p>
      ),
      dataIndex: "merchant_id",
      key: "merchant_id",
      render: (value: any) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Source currency 
        </p>
      ),
      dataIndex: "src_currency_code",
      key: "src_currency_code",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Destination currency
        </p>
      ),
      dataIndex: "dst_currency_code",
      key: "dst_currency_code",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Rate
        </p>
      ),
      dataIndex: "rate",
      key: "rate",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Margin percentage
        </p>
      ),
      dataIndex: "margin_percent",
      key: "margin_percent",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Final Rate
        </p>
      ),
      dataIndex: "final_rate",
      key: "final_rate",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Source
        </p>
      ),
      dataIndex: "source",
      key: "source",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Sync type
        </p>
      ),
      dataIndex: "sync_type",
      key: "sync_type",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Bank Status
        </p>
      ),
      dataIndex: "bank_status",
      key: "bank_status",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Synced At
        </p>
      ),
      dataIndex: "synced_at",
      key: "synced_at",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {moment(value).format("YYYY-MM-DD HH:mm:ss")}
        </p>
      ),
    },
  ];

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <div>
          <h1 className="text-[22px] sm:text-[28px] font-manrope text-primary font-medium leading-tight">
            Currency Conversion Rate history
          </h1>
        </div>
      </div>{" "}
      {!canViewHistory ? (
        <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 mt-2 sm:mt-4 text-center shadow-sm">
          <p className="font-manrope text-[16px] font-semibold text-secondary">
            You do not have permission to view currency conversion history.
          </p>
        </div>
      ) : (
        <div className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
          <Table
            loading={isLoading}
            columns={columns}
            dataSource={dataSource}
            pagination={{
              total,
              current: searchParams.page,
              onChange: onPageChange,
              position: ["bottomCenter"],
              defaultPageSize: LIST_PAGE_SIZE,
              showSizeChanger: false,
            }}
            rowClassName="editable-row"
            // scroll={{ x: "max-content" }}
          />
        </div>
      )}
    </div>
  );
};

export default CardConversionHistory;
